// Compact 3-locale language switcher. Renders three pill-buttons
// (EN / FR / HT). Picking one calls i18n.changeLanguage(), which
// the LanguageDetector persists to localStorage so the choice
// sticks across sessions.

import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { SUPPORTED_LOCALES, type SupportedLocale } from '../../lib/i18n';
import { cn } from '../../lib/cn';

interface Props {
  className?: string;
  // 'compact' renders just the three pills; 'labeled' prepends a globe + lang name.
  variant?: 'compact' | 'labeled';
}

export function LanguageSwitcher({ className, variant = 'compact' }: Props) {
  const { i18n, t } = useTranslation();
  const current = (i18n.resolvedLanguage || 'en') as SupportedLocale;

  return (
    <div className={cn('inline-flex items-center gap-1.5', className)}>
      {variant === 'labeled' && (
        <span className="inline-flex items-center gap-1 text-[12px] text-[var(--color-secondary-2)]">
          <Globe className="w-3.5 h-3.5" />
          {t(`lang.${current}`)}
        </span>
      )}
      <div className="inline-flex rounded-full border border-[var(--color-border)] bg-white p-0.5">
        {SUPPORTED_LOCALES.map((lng) => (
          <button
            key={lng}
            type="button"
            onClick={() => i18n.changeLanguage(lng)}
            aria-pressed={current === lng}
            className={cn(
              'px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-full transition-colors',
              current === lng
                ? 'bg-[var(--color-ink)] text-white'
                : 'text-[var(--color-secondary-2)] hover:text-[var(--color-ink)]',
            )}
            title={t(`lang.${lng}`)}
          >
            {lng}
          </button>
        ))}
      </div>
    </div>
  );
}
